/**
 * Font schemes — a named heading + body pairing applied to a whole publication (Publisher: Page Design ▸ Schemes ▸ Fonts).
 *
 * Built-in schemes come from the template engine (so a templated publication already "has" a scheme); the user's own schemes
 * are stored account-wide by the backend (like font favourites) and therefore appear in every publication.
 *
 * Applying a scheme: every text-bearing object is classified as *heading* or *body* — first by the publication's current
 * scheme (text in the current heading face is a heading, text in the current body face is body), then by the object's own
 * formatting (large or bold text is a heading) — and gets the matching family everywhere its font is stored (object props,
 * table cells, rich-text runs, inline HTML). One undoable step.
 */
import axios from 'axios';
import { FONT_SCHEMES } from './templates';
import { fontUsage } from './fontSwap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Built-in schemes: the template engine's plus a few classics. `[{ id, name, heading, body }]` */
export const BUILTIN_FONT_SCHEMES = [
  ...Object.entries(FONT_SCHEMES).map(([id, f]) => ({ id, name: f.name, heading: f.heading, body: f.body })),
  { id: 'office', name: 'Office', heading: 'Open Sans', body: 'Lora' },
  { id: 'elegant', name: 'Elegant', heading: 'Cormorant Garamond', body: 'Raleway' },
  { id: 'casual', name: 'Casual', heading: 'Pacifico', body: 'Quicksand' },
  { id: 'newsprint', name: 'Newsprint', heading: 'Libre Baskerville', body: 'Source Sans Pro' },
  { id: 'studio', name: 'Studio', heading: 'Space Grotesk', body: 'Inter' },
];

/* ---------------------------------------------------------------------------------------------------------------------- */
/* Custom schemes (account-level store, same pattern as fontFavorites)                                                     */
/* ---------------------------------------------------------------------------------------------------------------------- */
let custom = []; // [{ id, name, heading, body }]
let loaded = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => { try { fn(custom); } catch { /* ignore */ } });

export const onFontSchemesChanged = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const getCustomFontSchemes = () => custom;

export const loadFontSchemes = (force = false) => {
  if (loaded && !force) return loaded;
  loaded = (async () => {
    try {
      const { data } = await axios.get(`${API}/fonts/schemes`);
      custom = Array.isArray(data?.schemes) ? data.schemes : [];
      notify();
    } catch (e) {
      console.warn('[fonts] font schemes unavailable', e?.message || e); // eslint-disable-line no-console
    }
    return custom;
  })();
  return loaded;
};

const persist = async (next) => {
  const prev = custom;
  custom = next;
  notify();
  try {
    const { data } = await axios.put(`${API}/fonts/schemes`, { schemes: next });
    if (Array.isArray(data?.schemes)) { custom = data.schemes; notify(); }
  } catch (e) {
    custom = prev;
    notify();
    throw e;
  }
};

/** Save a new custom scheme (or overwrite one with the same name). Resolves to the saved scheme. */
export const saveFontScheme = async ({ name, heading, body }) => {
  const n = String(name || '').trim();
  const scheme = { id: `user:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`, name: n, heading, body };
  const rest = custom.filter((s) => s.name.toLowerCase() !== n.toLowerCase());
  await persist([...rest, scheme]);
  return custom.find((s) => s.name.toLowerCase() === n.toLowerCase()) || scheme;
};
export const deleteFontScheme = async (id) => persist(custom.filter((s) => s.id !== id));

/** Edit a custom scheme in place (same id): any of name / heading / body. Resolves to the updated scheme. */
export const updateFontScheme = async (id, patch) => {
  const cur = custom.find((s) => s.id === id);
  if (!cur) throw new Error('This font scheme no longer exists.');
  const next = { ...cur, ...(patch.name !== undefined ? { name: String(patch.name).trim() || cur.name } : {}), ...(patch.heading ? { heading: patch.heading } : {}), ...(patch.body ? { body: patch.body } : {}) };
  // a rename must not swallow another scheme of the same name (the backend dedupes by name and would drop one)
  if (custom.some((s) => s.id !== id && s.name.toLowerCase() === next.name.toLowerCase())) throw new Error('Another font scheme of yours already has this name.');
  await persist(custom.map((s) => (s.id === id ? next : s)));
  return custom.find((s) => s.id === id) || next;
};

/** All schemes a publication can use: built-in first, then the user's. */
export const allFontSchemes = () => [...BUILTIN_FONT_SCHEMES, ...custom];
export const findFontScheme = (id) => allFontSchemes().find((s) => s.id === id) || null;

/* ---------------------------------------------------------------------------------------------------------------------- */
/* Classification + application                                                                                            */
/* ---------------------------------------------------------------------------------------------------------------------- */
const clean = (f) => String(f || '').split(',')[0].replace(/["']/g, '').trim();
const same = (a, b) => clean(a).toLowerCase() === clean(b).toLowerCase();
const HEADING_SIZE = 18; // px: text at least this large counts as a heading
const BOLD = (w) => w === 'bold' || Number(w) >= 600;

/**
 * 'heading' | 'body' for a text object's properties. `current` = the publication's current scheme (may be null).
 * Tables and captions are body text; WordArt is a heading.
 */
export const textRole = (props = {}, current = null, type = 'textbox') => {
  if (type === 'table') return 'body';
  if (props.wordArt) return 'heading';
  if (current) {
    if (props.fontFamily && same(props.fontFamily, current.heading) && !same(current.heading, current.body)) return 'heading';
    if (props.fontFamily && same(props.fontFamily, current.body) && !same(current.heading, current.body)) return 'body';
  }
  const size = Number(props.fontSize) || 16;
  if (size >= HEADING_SIZE) return 'heading';
  if (BOLD(props.fontWeight) && size >= 14 && String(props.text || '').trim().length <= 80) return 'heading';
  return 'body';
};

/** The heading / body families a publication actually uses (most used family per role), for pre-filling "Save as scheme". */
export const documentFontRoles = (pages = [], masters = {}, current = null) => {
  const count = { heading: new Map(), body: new Map() };
  const bump = (role, fam) => { const k = clean(fam); if (!k) return; count[role].set(k, (count[role].get(k) || 0) + 1); };
  const visit = (o) => {
    if (!o) return;
    const p = o.properties || {};
    if (p.fontFamily) bump(textRole(p, current, o.type), p.fontFamily);
    if (Array.isArray(p.cells)) p.cells.forEach((row) => (Array.isArray(row) ? row : []).forEach((c) => c?.fontFamily && bump('body', c.fontFamily)));
    (o.children || []).forEach(visit);
  };
  pages.forEach((pg) => (pg.objects || []).forEach(visit));
  Object.values(masters || {}).forEach((m) => (m.objects || []).forEach(visit));
  const top = (m) => [...m.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  const usage = fontUsage(pages, masters);
  const any = [...usage.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  return { heading: top(count.heading) || current?.heading || any || 'Montserrat', body: top(count.body) || current?.body || any || 'Inter' };
};

const HTML_FONT_RE = /font-family:\s*(&quot;|['"])?([^;'">&]+)\1([^;">]*)/gi;
const setHtmlFont = (html, family, stats) => html.replace(HTML_FONT_RE, (m, q, fam, rest) => {
  if (same(fam, family)) return m;
  stats.count += 1;
  const quote = q || "'";
  const tail = !q && fam.includes(',') ? fam.slice(fam.indexOf(',')) : '';
  return `font-family: ${quote}${family}${quote}${tail}${rest || ''}`;
});

const applyToProps = (p, family, stats) => {
  if (!p || typeof p !== 'object') return p;
  let out = p, changed = false;
  const set = (k, v) => { if (!changed) { out = { ...p }; changed = true; } out[k] = v; };
  if (p.fontFamily && !same(p.fontFamily, family)) { set('fontFamily', family); stats.count += 1; }
  if (Array.isArray(p.cells)) {
    let cellsChanged = false;
    const cells = p.cells.map((row) => (Array.isArray(row) ? row.map((c) => {
      if (c && c.fontFamily && !same(c.fontFamily, family)) { cellsChanged = true; stats.count += 1; return { ...c, fontFamily: family }; }
      return c;
    }) : row));
    if (cellsChanged) set('cells', cells);
  }
  if (Array.isArray(p.runs)) {
    let runsChanged = false;
    const runs = p.runs.map((r) => {
      const fam = r?.style?.fontFamily || r?.fontFamily;
      if (fam && !same(fam, family)) { runsChanged = true; stats.count += 1; return r.style ? { ...r, style: { ...r.style, fontFamily: family } } : { ...r, fontFamily: family }; }
      return r;
    });
    if (runsChanged) set('runs', runs);
  }
  for (const key of ['html', 'content']) {
    if (typeof p[key] === 'string' && /font-family/i.test(p[key])) {
      const before = stats.count;
      const next = setHtmlFont(p[key], family, stats);
      if (stats.count !== before) set(key, next);
    }
  }
  return out;
};

const applyToObject = (o, scheme, current, stats) => {
  if (!o) return o;
  const hasText = o.properties && (o.properties.fontFamily || o.properties.cells || o.properties.runs || o.properties.html || o.properties.content);
  const props = hasText ? applyToProps(o.properties, textRole(o.properties, current, o.type) === 'heading' ? scheme.heading : scheme.body, stats) : o.properties;
  let children = o.children;
  if (Array.isArray(o.children)) {
    const next = o.children.map((c) => applyToObject(c, scheme, current, stats));
    if (next.some((c, i) => c !== o.children[i])) children = next;
  }
  if (props === o.properties && children === o.children) return o;
  return { ...o, properties: props, children };
};

/**
 * Apply `scheme` ({ heading, body }) to pages + masters. `current` is the publication's current scheme (or null).
 * Returns `{ pages, masters, count }`; pages / masters keep their references when nothing changed.
 */
export const applyFontScheme = (pages = [], masters = {}, scheme, current = null) => {
  if (!scheme || !scheme.heading || !scheme.body) return { pages, masters, count: 0 };
  const stats = { count: 0 };
  let pagesChanged = false;
  const nextPages = pages.map((pg) => {
    const objs = (pg.objects || []).map((o) => applyToObject(o, scheme, current, stats));
    if (objs.some((o, i) => o !== pg.objects[i])) { pagesChanged = true; return { ...pg, objects: objs }; }
    return pg;
  });
  let mastersChanged = false;
  const nextMasters = {};
  Object.entries(masters || {}).forEach(([id, m]) => {
    const objs = (m.objects || []).map((o) => applyToObject(o, scheme, current, stats));
    if (objs.some((o, i) => o !== m.objects[i])) { mastersChanged = true; nextMasters[id] = { ...m, objects: objs }; } else nextMasters[id] = m;
  });
  return { pages: pagesChanged ? nextPages : pages, masters: mastersChanged ? nextMasters : masters, count: stats.count };
};
